import type { components } from '@/api/schema';

/**
 * The addendum's one bound, **derived from the OpenAPI document rather than restated**.
 *
 * ⚠️ **A client constant would drift, and this is the one *free-text* field on this screen with a
 * declared `maxLength`.** `AddendumIn.text` declares `5000` in the document — unlike `overall_comment`
 * and `ScoreUpdate.comment`, which declare none (`api/interviews.ts` records that asymmetry and why the
 * client must not invent a limit where the product has none). It is not the only bounded field in the
 * product — `RoleIn` and `PersonIn` have always declared theirs, and `QuestionIn.text` joined them with
 * BUG-058 (`questionFields.ts`, which carries its bound onto the field the same way this one does). So
 * the bound exists, it belongs to the backend, and
 * `addendum.test.tsx` asserts this value equals the number in `openapi.snapshot.json`: a different one
 * turns the suite red and says *"derive it"* rather than letting a `maxLength` on an input and a
 * `maxLength` on a column disagree about somebody's paragraph.
 * ⚠️ **It fires when the snapshot is regenerated, not when the backend publishes** (corrected
 * 2026-08-09, BUG-070). This is an **equality**, so it does go red on refresh — unlike an *absence*
 * assertion, which by then has nothing left to announce. BUG-058 measured that difference the hard
 * way: its absence tripwire stayed green on the day it was written to fire.
 *
 * It is a **type-level** derivation of the shape and a **test-level** derivation of the number, because
 * TypeScript cannot read a `maxLength` out of a JSON Schema: the type below is what fails if the field is
 * renamed, and the test is what fails if the bound changes.
 */
export type AddendumText = components['schemas']['AddendumIn']['text'];

export const ADDENDUM_MAX_TEXT = 5000;
